import { FC, useEffect, useMemo } from 'react';
import { format, subYears } from 'date-fns';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import { usePermissions } from '@/providers/Permissions.provider';
import useElinxDetails from './hooks/useElinxDetails';
import {
  Box,
  formattedCurrency,
  capitalizeFirstLetterOfWords,
  withStyles,
  Card,
  sanitizeToNumeric,
  TransactionType,
  TransactionsLists,
} from 'vopay';
import { Title, DetailsRows, TransactionStatus, TransactionCard, UtilButton, TransactionTypeLabel } from 'UI';
import ApprovalHistoryDetailsCard from './Card/ApprovalHistoryDetailsCard';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type URLParams = {
  TransactionID: string;
};

type ElinxTransactionInfoProps = {
  eLinxRequestID: string;
  jwt?: string;
  // The eLinx placeholder's own funding parent (when pre-funded) — resolved generically by
  // TransactionDetails.tsx (shared with FundingStatusBanner) instead of fetching it again here.
  parentTransaction?: TransactionsLists;
};

const ElinxTransactionInfo: FC<ElinxTransactionInfoProps> = ({ eLinxRequestID, jwt, parentTransaction }) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.ElinxTransactionInfo',
  });

  const { t: tGlobal } = useTranslation('translations');
  const { TransactionID } = useParams<URLParams>();
  const currentDate = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
  const { transactions, fetchTransactionsHistory } = useFetchTransactionsHistory();
  const { permissions } = usePermissions();

  const { details, elinxAdditionalId, invoiceButton } = useElinxDetails({
    ELinxRequestID: eLinxRequestID,
    jwt: jwt || '',
  });

  const transaction = useMemo(() => {
    const isCancelledElinx =
      transactions.length === 2 &&
      transactions[1].TransactionType === 'eLinx' &&
      transactions[1].TransactionStatus === 'cancelled';

    return isCancelledElinx ? transactions[1] : transactions[0];
  }, [transactions]);

  const {
    response: { clientAccountsEnabled, JWT: mainJwt },
  } = useClientData();

  const isMainAccount = !jwt || jwt === mainJwt;

  useEffect(() => {
    if (!TransactionID) return;

    fetchTransactionsHistory({
      StartDateTime: format(subYears(new Date(), 5), 'yyyy-MM-dd'),
      EndDateTime: currentDate,
      TransactionID,
      IncludeSubaccountTransactions: Boolean(permissions.ViewSubaccounts),
    });
  }, [TransactionID, currentDate, permissions.ViewSubaccounts]);

  if (!transaction) return null;

  const transactionDetails = [
    {
      label: t('TransactionDetails.status', { defaultValue: 'Status' }),
      value: (
        <Box jc="fe">
          <TransactionStatus
            status={transaction.TransactionStatus}
            label={
              capitalizeFirstLetterOfWords(
                tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus || ''}`, {
                  defaultValue: transaction.TransactionStatus,
                }),
              ) || ''
            }
          />
        </Box>
      ),
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: <TransactionTypeLabel transactionType={transaction.TransactionType} />,
    },
    {
      label: t('TransactionDetails.recipientName', { defaultValue: 'Recipient Name' }),
      value:
        transaction.ClientAccountID && isMainAccount ? (
          <UtilButton
            onClick={() => {
              if (clientAccountsEnabled) {
                window.open(`/client-accounts/${transaction.ClientAccountID}`, '_blank');
              } else {
                window.open(`/contacts/${transaction.ClientAccountID}`, '_blank');
              }
            }}
          >
            {details?.ReceiverName}
          </UtilButton>
        ) : (
          details?.ReceiverName
        ),
      visible: !!details?.ReceiverName,
    },
    {
      label: t('TransactionDetails.elinxId', { defaultValue: 'eLinx ID' }),
      value: transaction.ELinxRequestID,
      visible: !!transaction.ELinxRequestID,
    },
    {
      label: t('TransactionDetails.transactionTypeCode', { defaultValue: 'Transaction Type Code' }),
      value: transaction.TransactionTypeCode || '-',
    },
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${parseFloat(transaction.CreditAmount) === 0 ? formattedCurrency(transaction.DebitAmount) : formattedCurrency(transaction.CreditAmount) || '-'}`,
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount ? `${transaction.Currency} ${formattedCurrency(transaction.HoldAmount)}` : '-',
    },
    {
      label: t('TransactionDetails.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: transaction.Notes || '-',
    },
    {
      label: t('TransactionDetails.invoice', { defaultValue: 'Invoice' }),
      value: invoiceButton,
      visible: !!invoiceButton,
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${transaction.CreditAmount && Number(sanitizeToNumeric(transaction.CreditAmount)) !== 0 ? formattedCurrency(transaction.CreditAmount) : formattedCurrency(transaction.DebitAmount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: transaction.TransactionFailureReason || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={details?.ReceiverName || ''}
        amount={parseFloat(transaction.CreditAmount) === 0 ? transaction.DebitAmount : transaction.CreditAmount}
        currency={transaction.Currency}
        transactionId={transaction.TransactionID}
        type={'eLinx'}
        failureReason={transaction.TransactionFailureReason || ''}
        contactId={transaction.ClientAccountID}
        jwt={jwt}
        isMainAccount={isMainAccount}
        additionalId={elinxAdditionalId}
        eLinxRequestId={eLinxRequestID}
        parentTransactionId={parentTransaction?.TransactionID}
        parentType={parentTransaction?.TransactionType}
        parentTransactionStatus={parentTransaction?.TransactionStatus}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>

        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      <ApprovalHistoryDetailsCard
        transactionID={transaction.TransactionID}
        status={transaction.TransactionStatus}
        type={TransactionType.ELINX}
        jwt={jwt}
      />

      {transaction.TransactionFailureReason && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Info' })}
          </Title>

          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default ElinxTransactionInfo;
